import { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cachedToken, setCachedToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);

  const getToken = async () => {
    // If we have a cached token and it's not expired, return it
    if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
      return cachedToken;
    }

    // Otherwise, get a new token
    if (user) {
      const token = await user.getIdToken(true);
      setCachedToken(token);
      // Set token expiry to 55 minutes (Firebase tokens expire in 1 hour)
      setTokenExpiry(Date.now() + 55 * 60 * 1000);
      return token;
    }
    return null;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setCachedToken(token);
          setTokenExpiry(Date.now() + 55 * 60 * 1000);
          setUser(user);
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Token refresh failed:", error);
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setCachedToken(null);
        setTokenExpiry(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
